/**
 * `<header-menu>` custom element (FR22).
 *
 * Controls the mobile menu drawer and the desktop submenus: opens the drawer,
 * traps focus while it is open, and closes it (or a focused submenu) on
 * Escape, returning focus to where it was.
 */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.drawer = this.querySelector('[data-menu-drawer]');
    if (this.toggle) {
      this.toggle.addEventListener('click', this.onToggle.bind(this));
    }
    const close = this.querySelector('[data-menu-close]');
    if (close) {
      close.addEventListener('click', () => {
        this.drawer.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.focus();
      });
    }
    document.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));

    // Il pannello ricerca e' un <details>: si apre da solo. Qui solo il
    // focus sul campo all'apertura; Esc e click fuori lo chiudono sotto.
    this.search = this.querySelector('[data-header-search]');
    if (this.search) {
      this.search.addEventListener('toggle', () => {
        if (this.search.open) {
          const input = this.search.querySelector('input[type="search"]');
          if (input) input.focus();
        }
      });
    }
    // A submenu closed with Esc reopens only once focus and pointer have left
    // the item: `:focus-within`/`:hover` take over again.
    this.addEventListener('focusout', this.onLeaveItem.bind(this));
    this.addEventListener('mouseout', this.onLeaveItem.bind(this));

    // La barra annuncio resta fissa sopra l'header sticky: il CSS aggancia
    // l'header alla sua altezza reale, misurata qui (40px di fallback no-JS).
    const bar = document.querySelector('.announcement-bar');
    if (bar) {
      const setBarHeight = () => document.documentElement.style.setProperty(
        '--announcement-bar-height', `${bar.offsetHeight}px`);
      setBarHeight();
      new ResizeObserver(setBarHeight).observe(bar);
    }
  }

  onLeaveItem(event) {
    const item = event.target.closest && event.target.closest('.menu__item--closed');
    if (item && !(event.relatedTarget && item.contains(event.relatedTarget))) {
      item.classList.remove('menu__item--closed');
    }
  }

  onToggle() {
    const open = this.drawer.hidden;
    this.drawer.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      const firstLink = this.drawer.querySelector('a');
      if (firstLink) firstLink.focus();
    }
  }

  onKeydown(event) {
    if (event.key === 'Tab' && this.drawer && !this.drawer.hidden) {
      this.trapTab(event);
      return;
    }
    if (event.key !== 'Escape') return;

    if (this.search && this.search.open) {
      this.search.open = false;
      this.search.querySelector('summary').focus();
      return;
    }

    if (this.drawer && !this.drawer.hidden) {
      this.drawer.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.focus();
      return;
    }

    // Desktop submenus open on :hover/:focus-within (no JS): Esc closes the one
    // the focus is in and returns focus to the link that opened it (Story 4.1).
    // Aperto solo col mouse il focus è altrove: vale la voce sotto il puntatore,
    // ma lì il focus NON si sposta — lo terrebbe aperto lui all'uscita del mouse.
    const active = document.activeElement;
    const focusedItem = active && active.closest('.menu__item--has-children');
    const item = focusedItem || this.querySelector('.menu__item--has-children:hover');
    if (item && this.contains(item)) {
      item.classList.add('menu__item--closed');
      if (focusedItem) {
        const parentLink = item.querySelector(':scope > .menu__link');
        if (parentLink) parentLink.focus();
      }
    }
  }

  // Il drawer è un div, non un <dialog>: il trap promesso dal contratto del
  // componente va fatto a mano — Tab sull'ultimo torna al primo e viceversa.
  trapTab(event) {
    const items = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onDocumentClick(event) {
    if (this.search && this.search.open && !this.search.contains(event.target)) {
      this.search.open = false;
    }
    if (this.drawer && !this.drawer.hidden && !this.contains(event.target)) {
      this.drawer.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
